/**
 * Canonical date helpers. Two rules keep the app timezone-safe:
 *
 *  1. A calendar date ("2026-07-03") is always built from LOCAL getters and
 *     parsed back to LOCAL midnight. Never `toISOString()` (shifts the day
 *     for any teacher east of UTC) and never `new Date('YYYY-MM-DD')`
 *     (parses as UTC midnight, so local getters read the previous day west
 *     of UTC).
 *  2. Day-of-week maps live here, once: DayOfWeek enum ↔ JS getDay().
 */

export type DayOfWeek =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY'
export type WeekStartDay = 'MONDAY' | 'SUNDAY'

export const ALL_DAYS: DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
]

/** DayOfWeek → JS Date.getDay() (0=Sun … 6=Sat). */
export const DAY_TO_JS: Record<DayOfWeek, number> = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
}

/** JS Date.getDay() → DayOfWeek. */
export const JS_TO_DAY: DayOfWeek[] = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
]

/** Local calendar date as 'YYYY-MM-DD'. */
export function toLocalDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Today's local calendar date as 'YYYY-MM-DD'. */
export function todayKey(): string {
  return toLocalDateKey(new Date())
}

/** Parse 'YYYY-MM-DD' to LOCAL midnight. */
export function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y ?? NaN, (m ?? 1) - 1, d ?? 1)
}

/** Local 'HH:mm' of a Date or parseable datetime string. */
export function hhmm(value: Date | string): string {
  const d = typeof value === 'string' ? new Date(value) : value
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** New Date `n` days after `date` (local; handles month/year rollover). */
export function addDays(date: Date, n: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}

/** The DayOfWeek a 'YYYY-MM-DD' date key falls on. */
export function dayOfWeekOf(key: string): DayOfWeek {
  return JS_TO_DAY[parseDateKey(key).getDay()]!
}

/** Local midnight of the week containing `date`, anchored on `weekStart`. */
export function startOfWeek(date: Date, weekStart: WeekStartDay = 'MONDAY'): Date {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  const diff = (start.getDay() - DAY_TO_JS[weekStart] + 7) % 7
  return addDays(start, -diff)
}

/**
 * The next occurrence of `day` strictly after `from` (defaults to today) —
 * "next Monday" on a Monday is a week away, matching the schedule modals.
 */
export function nextOccurrenceOf(day: DayOfWeek, from: Date = new Date()): Date {
  const days = (DAY_TO_JS[day] - from.getDay() + 7) % 7 || 7
  const next = addDays(from, days)
  next.setHours(0, 0, 0, 0)
  return next
}

/** Count how many times `day` occurs in the inclusive date-key range. */
export function countOccurrences(day: DayOfWeek | string, startKey: string, endKey: string): number {
  const target = DAY_TO_JS[day as DayOfWeek]
  const start = parseDateKey(startKey)
  const end = parseDateKey(endKey)
  if (target === undefined || isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) {
    return 0
  }
  let count = 0
  for (let d = start; d <= end; d = addDays(d, 1)) {
    if (d.getDay() === target) count++
  }
  return count
}
